import React, { useState, useEffect, useRef } from 'react';
import {
  Plus,
  Search,
  Download,
  X,
  Calendar,
  DollarSign,
  FileText,
  AlertCircle,
  Package,
  Eye,
  Mail,
  Trash2,
} from 'lucide-react';

const BASE_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/plansInvoices';
const PLANS_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/plans';
const CUSTOMERS_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/customer';


const getAccessToken = () => sessionStorage.getItem('access_token');

const apiRequest = async (endpoint, method = 'GET', data = null) => {
  const url = `${BASE_URL}${endpoint}`;

  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getAccessToken()}`,
    },
  };

  if (data && method === 'POST') {
    options.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
   
      const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
};

function SearchableDropdown({ label, value, onChange, options, placeholder, required = false, displayKey = 'name', valueKey = 'id' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(option =>
    option[displayKey]?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(opt => opt[valueKey] === value);

  return (
    <div ref={dropdownRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 text-left border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white flex items-center justify-between"
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-400'}>
          {selectedOption ? selectedOption[displayKey] : placeholder}
        </span>
        <svg className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">No options found</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option[valueKey]}
                  type="button"
                  onClick={() => {
                    onChange(option[valueKey]);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-blue-50 transition-colors ${
                    option[valueKey] === value ? 'bg-blue-100 text-blue-700' : 'text-gray-900'
                  }`}
                >
                  <div className="font-medium">{option[displayKey]}</div>
                  {option.subtitle && <div className="text-xs text-gray-500 mt-0.5">{option.subtitle}</div>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CreatePlanInvoiceModal({ isOpen, onClose, onSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [plans, setPlans] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  const [formData, setFormData] = useState({
    planId: '',
    customerId: '',
    description: '',
    price: '',
    tax: '',
    totalAmount: '',
    dueDate: '',
    bank_name: '',
    bank_code: '',
    account_number: '',
    sort_code: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchPlansAndCustomers();
    }
  }, [isOpen]);

  const fetchPlansAndCustomers = async () => {
    try {
      setLoadingData(true);
      
      // Fetch plans and customers simultaneously
      const [plansResponse, customersResponse] = await Promise.all([
        fetch(`${PLANS_URL}/getPlan`, { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAccessToken()}` } }),
        fetch(`${CUSTOMERS_URL}/getAllCustomer`, { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAccessToken()}` } })
      ]);

      const plansData = await plansResponse.json();
      const customersData = await customersResponse.json();
      
      // Transform plans for dropdown
      const transformedPlans = (plansData.plans || []).map(plan => ({
        id: plan._id,
        name: plan.name || 'Unnamed Plan',
        subtitle: `$${plan.price || 0} - ${plan.frequency || 'monthly'} (${plan.durationMonths || 1} month${plan.durationMonths !== 1 ? 's' : ''})`,
      }));

      // Transform customers for dropdown
      const customerList = Array.isArray(customersData) ? customersData : (customersData.customers || []);
      const transformedCustomers = customerList.map(customer => ({
        id: customer._id,
        name: customer.fullName,
        subtitle: customer.email,
        customerPlanPrice: customer.customerPlanPrice ?? 0,
        bank_name: customer.bank_name || '',
        bank_code: customer.bank_code || '',
        account_number: customer.account_number || '',
        sort_code: customer.sort_code || ''
      }));
      
      setPlans(transformedPlans);
      setCustomers(transformedCustomers);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load plans and customers');
    } finally {
      setLoadingData(false);
    }
  };

  if (!isOpen) return null;

  const updateFormData = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate total amount when price or tax changes
      if (field === 'price' || field === 'tax') {
        const price = parseFloat(field === 'price' ? value : updated.price) || 0;
        const tax = parseFloat(field === 'tax' ? value : updated.tax) || 0;
        updated.totalAmount = (price + (price * tax / 100)).toFixed(2);
      }
      
      return updated;
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      if (!formData.customerId.trim()) { 
        setError('Customer is required');
        return;
      }

      // Validation
      if (!formData.planId.trim()) {
        setError('Plan ID is required');
        return;
      }
      if (!formData.description.trim()) {
        setError('Description is required');
        return;
      }
      if (!formData.price) {
        setError('Price is required');
        return;
      }
      if (!formData.dueDate) {
        setError('Due date is required');
        return;
      }

      const requestBody = {
        planId: formData.planId,
        customerId: formData.customerId,
        description: formData.description,
        price: Number(formData.price),
        tax: formData.tax || '0',
        totalAmount: Number(formData.totalAmount || formData.price),
        dueDate: new Date(formData.dueDate).toISOString(),
        bank_name: formData.bank_name,
        bank_code: formData.bank_code,
        account_number: formData.account_number,
        sort_code: formData.sort_code
      };

      console.log('Creating plan invoice with data:', requestBody);

      const response = await apiRequest('/createPlanInvoice', 'POST', requestBody);
      console.log('Plan invoice created:', response);

      // Success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-[#79C485] text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successMessage.textContent = 'Plan invoice created successfully!';
      document.body.appendChild(successMessage);

      setTimeout(() => {
        if (successMessage.parentNode) {
          successMessage.parentNode.removeChild(successMessage);
        }
      }, 3000);

      // Reset form
      setFormData({
        planId: '',
        customerId: '',
        description: '',
        price: '',
        tax: '',
        totalAmount: '',
        dueDate: '',
        bank_name: '',
        bank_code: '',
        account_number: '',
        sort_code: ''
      });

      onClose();
      if (onSuccess) {
        await onSuccess();
      }
    } catch (err) {
      console.error('Error creating invoice:', err);
      
      const errorMessage = err.message || '';
      if (errorMessage.includes('E11000') || errorMessage.includes('duplicate key') || errorMessage.includes('invoice_number')) {
        // Extract invoice number from error message
        const match = errorMessage.match(/invoice_number:\s*"([^"]+)"/);
        const invoiceNumber = match ? match[1] : '';
        setError(`An invoice already exists for this booking${invoiceNumber ? ` (${invoiceNumber})` : ''}. Please check existing invoices.`);
      } else {
        setError(errorMessage || 'Failed to create invoice');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      planId: '',
      customerId: '',
      description: '',
      price: '',
      tax: '',
      totalAmount: '',
      dueDate: '',
      bank_name: '',
      bank_code: '',
      account_number: '',
      sort_code: ''
    });
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Create Plan Invoice</h2>
            <p className="text-sm text-gray-500 mt-1">Generate a new invoice for a plan</p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-6">
            {loadingData ? (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-sm text-gray-500">Loading plans...</p>
              </div>
            ) : (
              <>
                <SearchableDropdown
                  label="Customer"
                  value={formData.customerId}
                  onChange={(value) => {
                    const selectedCustomer = customers.find(c => c.id === value);
                    
                    setFormData(prev => {
                      const newPrice = selectedCustomer?.customerPlanPrice 
                        ? selectedCustomer.customerPlanPrice.toString() 
                        : prev.price;
                      
                      const updated = { 
                        ...prev, 
                        customerId: value,
                        price: newPrice,
                        bank_name: selectedCustomer?.bank_name || '',
                        bank_code: selectedCustomer?.bank_code || '',
                        account_number: selectedCustomer?.account_number || '',
                        sort_code: selectedCustomer?.sort_code || ''
                      };
                      
                      const priceNum = parseFloat(newPrice) || 0;
                      const taxNum = parseFloat(updated.tax) || 0;
                      updated.totalAmount = (priceNum + (priceNum * taxNum / 100)).toFixed(2);
                      
                      return updated;
                    });
                  }}
                  options={customers}
                  placeholder="Select a customer"
                  required={true}
                  displayKey="name"
                  valueKey="id"
                />
                <SearchableDropdown
                  label="Plan"
                  value={formData.planId}
                  onChange={(value) => updateFormData('planId', value)}
                  options={plans}
                  placeholder="Select a plan"
                  required={true}
                  displayKey="name"
                  valueKey="id"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => updateFormData('description', e.target.value)}
                    placeholder="e.g., Yearly Plan Invoice"
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => updateFormData('price', e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tax (%)
                    </label>
                    <input
                      type="number"
                      value={formData.tax}
                      onChange={(e) => updateFormData('tax', e.target.value)}
                      placeholder="0"
                      min="0"
                      max="100"
                      step="0.01"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Total Amount
                    </label>
                    <input
                      type="number"
                      value={formData.totalAmount}
                      onChange={(e) => updateFormData('totalAmount', e.target.value)}
                      placeholder="Auto-calculated"
                      min="0"
                      step="0.01"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Auto-calculated from price + tax</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Due Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => updateFormData('dueDate', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Added Payment / Bank Details Section */}
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Payment Details (Optional)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                      <input
                        type="text"
                        value={formData.bank_name}
                        onChange={(e) => updateFormData('bank_name', e.target.value)}
                        placeholder="e.g. BCA"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Bank Code</label>
                      <input
                        type="text"
                        value={formData.bank_code}
                        onChange={(e) => updateFormData('bank_code', e.target.value)}
                        placeholder="e.g. 23456"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                      <input
                        type="text"
                        value={formData.account_number}
                        onChange={(e) => updateFormData('account_number', e.target.value)}
                        placeholder="e.g. 87654567876"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sort Code</label>
                      <input
                        type="text"
                        value={formData.sort_code}
                        onChange={(e) => updateFormData('sort_code', e.target.value)}
                        placeholder="e.g. 987654"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

              </>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Creating...
              </>
            ) : (
              'Create Invoice'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function PDFPreviewModal({ isOpen, onClose, pdfUrl, invoiceNumber }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Invoice Preview</h2>
            <p className="text-sm text-gray-500 mt-1">{invoiceNumber}</p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </a>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          <iframe
            src={pdfUrl}
            className="w-full h-full"
            title={`Invoice ${invoiceNumber}`}
          />
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ isOpen, onClose, onConfirm, invoiceNumber }) {
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setIsDeleting(true);
    await onConfirm();
    setIsDeleting(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center mb-4 justify-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
          </div>

          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Plan Invoice
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete plan invoice <span className="font-medium text-gray-900">{invoiceNumber}</span>? This action cannot be undone.
            </p>

            <div className="flex gap-3 justify-center">
              <button
                onClick={onClose}
                disabled={isDeleting}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete Invoice'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PlanInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest('/getPlanInvoices', 'GET');
      setInvoices(response.data || []);
      console.log('Fetched invoices2345678:', response.data);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch plan invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewClick = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPreviewModal(true);
  };

  const handleSendEmail = async (invoice) => {
    console.log('Attempting to send email for invoice:', invoice);
    if (!invoice.invoiceUrl) {
      alert('Cannot send email: Missing PDF link.');
      return;
    }

    //getting customer email from customerId in invoice
    // const response = await fetch(`${CUSTOMER_API_BASE}/getCustomer/${customerId}`, {
    //     method: 'GET',
    //     headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }
    //   });

    //get customer
    const customerResponse = await fetch(`${CUSTOMERS_URL}/getCustomer/${invoice.customerId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}` 
      },
    });
    if (!customerResponse.ok) {
      console.error('Failed to fetch customer details for email:', await customerResponse.text());
      alert('Failed to retrieve customer details. Cannot send email.');
      return;
    }

    const customerData = await customerResponse.json();
    console.log('Fetched customer data for email:', customerData);
    const customerEmail = customerData?.customer?.email;
    if (!customerEmail) {
      alert('Customer email not found. Cannot send invoice email.');
      return;
    }
    try {
      setSendingEmailId(invoice._id);
      
      const response = await fetch(`${BASE_URL}/sendPlanInvoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`  
        },      
        body: JSON.stringify({
          email: customerEmail,
          invoiceUrl: invoice.invoiceUrl
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      // Success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-[#79C485] text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successMessage.textContent = `Plan invoice sent successfully!`;
      document.body.appendChild(successMessage);

      setTimeout(() => {
        if (successMessage.parentNode) {
          successMessage.parentNode.removeChild(successMessage);
        }
      }, 3000);

    } catch (err) {
      console.error('Email error:', err);
      alert('Failed to send invoice email. Please try again.');
    } finally {
      setSendingEmailId(null);
    }
  };

  const handleDeleteClick = (invoice) => {
    setInvoiceToDelete(invoice);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!invoiceToDelete) return;

    try {
      setDeletingId(invoiceToDelete._id);
      const response = await fetch(`${BASE_URL}/deletePlanInvoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`
        },
        body: JSON.stringify({ invoiceId: invoiceToDelete._id })
      });

      if (!response.ok) {
        throw new Error('Failed to delete plan invoice');
      }

      // Success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-[#79C485] text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successMessage.textContent = 'Plan invoice deleted successfully!';
      document.body.appendChild(successMessage);

      setTimeout(() => {
        if (successMessage.parentNode) {
          successMessage.parentNode.removeChild(successMessage);
        }
      }, 3000);

      setShowDeleteModal(false);
      setInvoiceToDelete(null);
      fetchInvoices();
    } catch (err) {
      console.error('Delete error:', err);
      alert(err.message || 'Failed to delete plan invoice. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'IDR'
    }).format(amount || 0);
  };

  const filteredInvoices = invoices.filter(invoice => {
    const term = searchTerm.toLowerCase();
    // Safely get the plan name since it is now populated
    const planName = typeof invoice.planId === 'object' ? invoice.planId?.name : invoice.planId;
    
    return (
      invoice.sno?.toLowerCase().includes(term) ||
      invoice.description?.toLowerCase().includes(term) ||
      planName?.toLowerCase().includes(term)
    );
  });


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search plan invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#1A5090] text-white rounded-lg hover:bg-blue-900"
        >
          <Plus className="w-4 h-4" />
          <span className="text-sm font-medium">Create Invoice</span>
        </button>
      </div>

      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-500">Loading plan invoices...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="font-medium">Error loading plan invoices</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {!loading && !error && filteredInvoices.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">
            {searchTerm ? 'No plan invoices found matching your search' : 'No plan invoices found'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 px-4 py-2 bg-[#1A5090] text-white rounded-lg hover:bg-blue-900"
            >
              <Plus className="w-4 h-4 inline mr-2" />
              Create your first plan invoice
            </button>
          )}
        </div>
      )}

      {!loading && !error && filteredInvoices.length > 0 && (
        <div className="space-y-4">
          {filteredInvoices.map((invoice) => (
            <div
              key={invoice._id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 grid grid-cols-4 gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-gray-500">Invoice Number</span>
                    </div>
                    <p className="font-semibold text-gray-900">{invoice.sno}</p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Package className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-gray-500">Plan </span>
                    </div>
                    <p className="font-mono text-sm text-gray-900">{typeof invoice.planId === 'object' ? invoice?.planId?.name : invoice.planId}</p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-gray-500">Dates</span>
                    </div>
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">Created:</span> {formatDate(invoice.createdAt)}
                    </p>
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">Due:</span> {formatDate(invoice.dueDate)}
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-1">
                    <p className="text-xs text-gray-600">
                     IDR
                    </p>
                      <span className="text-xs text-gray-500">Amount</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Price: {formatCurrency(parseFloat(invoice.price))}
                    </p>
                    <p className="text-sm text-gray-600">
                      Tax ({invoice.tax}%): {formatCurrency(parseFloat(invoice.price) * parseFloat(invoice.tax) / 100)}
                    </p>
                    <p className="text-lg font-bold text-gray-900 mt-1">
                      {formatCurrency(parseFloat(invoice.totalAmount))}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-6">
                  {invoice.invoiceUrl && (
                    <>
                      {/* --- ADD THIS MAIL BUTTON --- */}
                      <button
                        onClick={() => handleSendEmail(invoice)}
                        disabled={sendingEmailId === invoice._id}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center"
                        title="Send via Email"
                      >
                        {sendingEmailId === invoice._id ? (
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Mail className="w-5 h-5" />
                        )}
                      </button>

                      <button
                        onClick={() => handlePreviewClick(invoice)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Preview PDF"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <a
                        href={invoice.invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Download PDF"
                      >
                        <Download className="w-5 h-5" />
                      </a>
                    </>
                  )}
                  <button
                    onClick={() => handleDeleteClick(invoice)}
                    disabled={deletingId === invoice._id}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center"
                    title="Delete Invoice"
                  >
                    {deletingId === invoice._id ? (
                      <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Trash2 className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {invoice.description && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Description:</p>
                  <p className="text-sm text-gray-700">{invoice.description}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <CreatePlanInvoiceModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchInvoices}
      />

      <PDFPreviewModal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setSelectedInvoice(null);
        }}
        pdfUrl={selectedInvoice?.invoiceUrl}
        invoiceNumber={selectedInvoice?.sno}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setInvoiceToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        invoiceNumber={invoiceToDelete?.sno}
      />
    </div>
  );
}